import { Modal, Select } from 'antd';
import { useAppSelector, useAppTranslation } from 'app/hooks';
import CloseIcon from 'assets/icons/CloseIcon';
import Typo from 'consts/typography';
import React, { useEffect, useMemo, useState } from 'react';
import { platformStates } from 'redux/platformSlice';
import { CopyOutlined, ExclamationCircleOutlined } from '@ant-design/icons';
import { useMessageSuccess } from 'hooks/useMessageSuccess';
import { selectAssets, selectCoins } from 'features/home/homeSlice';
import QRCode from 'react-qr-code';
import depositApi from 'api/depositApi';
import { getLocalStorage } from 'utils/sessionStorage';
import { useHandleResponseError } from 'hooks/useHandleResponseError';
import { IMG_ALT } from 'consts/app';
import useUserDetail from 'hooks/useUserDetail';
import FiatDepositTab from './FiatDepositTab';
import { uniqueId } from 'utils/commonUtils';
import useThemes from 'hooks/useThemes';
import { DepositReponseModel } from 'models/deposit';

interface DepositModalProps {
  title?: string;
  visible: boolean;
  closeModal: () => void;
  className?: string;
}

const DepositModal: React.FunctionComponent<DepositModalProps> = (props) => {
  const { title = '', visible, closeModal, className = '' } = props;
  const { currentTheme } = useThemes();
  const [currentAsset, setCurrentAsset] = useState<number>(0);
  const [currentCoin, setCurrentCoin] = useState<number>(0);
  const [depositInfo, setDepositInfo] = useState<DepositReponseModel | undefined>(undefined);
  const { isPhone, isTablet } = useAppSelector(platformStates);
  const handleResponseError = useHandleResponseError();
  const t = useAppTranslation();
  const { showToast, contextHolder } = useMessageSuccess();
  const accessToken = getLocalStorage('access_token_terabet');
  const coins = useAppSelector(selectCoins);
  const assets = useAppSelector(selectAssets);
  const { isUserLoggedIn } = useUserDetail();
  const [activeTab, setActiveTab] = useState<number>(1);

  const onChangeActiveTab = (value: number) => {
    setActiveTab(value);
  };

  const tabButtons = [
    {
      value: 1,
      label: t('cashier.crypto'),
      grow: 5,
    },
    {
      value: 2,
      label: t('cashier.fiat'),
      grow: 5,
    },
  ];

  const coinOptions = useMemo(() => {
    return coins
      .filter((x) => x.enableDeposit)
      .map((coin) => ({
        value: coin.id,
        label: (
          <div className="flex-row items-center gap-2 h-full">
            <img src={coin.image} alt={IMG_ALT} className="dropdown-logo" />
            <span
              className={`primary-text-color ${isPhone ? Typo.p6 : isTablet ? Typo.p4 : Typo.p2}`}
            >
              {coin.symbol}
            </span>
          </div>
        ),
      }));
  }, [coins, isPhone, isTablet]);

  const networkOptions = useMemo(() => {
    return assets
      .filter((asset) => asset.coin.id === currentCoin)
      .map((asset) => ({
        value: asset.assetId,
        label: (
          <span
            className={`primary-text-color ${isPhone ? Typo.p6 : isTablet ? Typo.p4 : Typo.p2}`}
          >
            {asset.chain.name}
          </span>
        ),
      }));
  }, [assets, currentCoin, isPhone, isTablet]);

  useEffect(() => {
    if (coins.length !== 0) {
      setCurrentCoin(coins.find((coin) => coin.enableDeposit)?.id || 0);
    }
  }, [coins]);

  useEffect(() => {
    if (assets.length !== 0) {
      setCurrentAsset(assets.find((asset) => asset.coin.id === currentCoin)?.assetId || 0);
    }
  }, [currentCoin, assets]);

  const onAssetChange = (event: number) => {
    setCurrentAsset(event);
  };
  const onCoinChange = (event: number) => {
    setCurrentCoin(event);
  };

  const onSubmit = async () => {
    const { ok, body, error } = await depositApi.getAddress(
      {
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      },
      currentAsset,
    );

    if (ok && body) {
      setDepositInfo(body);
    }

    if (error) handleResponseError(error);
  };

  const onCloseModal = () => {
    setDepositInfo(undefined);
    setActiveTab(1);
    closeModal();
  };

  const handleCopyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text || '');
    showToast('Copy to clipboard!');
  };

  const resetTab = () => {
    setActiveTab(1);
  };

  return (
    <Modal
      className={className}
      title={title}
      visible={visible}
      onCancel={onCloseModal}
      footer={null}
      centered
      maskClosable={false}
      closeIcon={<CloseIcon isDarkMode={currentTheme === 'dark'} width="20" height="20" />}
      width={600}
    >
      <div className="center-content-col gap-3">
        {/* <div className="w-full flex flex-row items-center justify-between deposit-tab">
          {tabButtons.map((x) => (
            <button
              key={uniqueId()}
              className={`flex-1 h-full cursor-pointer ${
                isPhone ? Typo.p5 : isTablet ? Typo.p3 : Typo.p1
              } ${activeTab === x.value ? 'tab__active' : ''}`}
              style={{ flexGrow: x.grow }}
              onClick={() => onChangeActiveTab(x.value)}
            >
              {x.label}
            </button>
          ))}
        </div> */}
        {activeTab === 1 && !depositInfo && (
          <>
            <div className="common-select">
              <p
                className={`primary-text-color ${isPhone ? Typo.p6 : isTablet ? Typo.p4 : Typo.p2}`}
              >
                {t('cashier.coin')}
              </p>
              <Select
                className="antd__select"
                dropdownClassName="antd__select-dropdown"
                value={currentCoin}
                onChange={onCoinChange}
                options={coinOptions}
              />
            </div>
            <div className="common-select">
              <p
                className={`primary-text-color ${isPhone ? Typo.p6 : isTablet ? Typo.p4 : Typo.p2}`}
              >
                {t('cashier.network')}
              </p>
              <Select
                className="antd__select"
                dropdownClassName="antd__select-dropdown"
                value={currentAsset}
                onChange={onAssetChange}
                options={networkOptions}
              />
            </div>
            {isUserLoggedIn && (
              <div className="withdraw-balance">
                <div className="withdraw-balance__left"></div>
                <div className="withdraw-balance__right">
                  <button
                    className={`button-generate ${isUserLoggedIn ? '' : 'disabled'}`}
                    type="submit"
                    onClick={onSubmit}
                    disabled={!isUserLoggedIn}
                  >
                    {t('cashier.getDepositAddress')}
                  </button>
                </div>
              </div>
            )}
          </>
        )}
        {activeTab === 1 && depositInfo && (
          <>
            <QRCode
              viewBox={`0 0 256 256`}
              size={256}
              value={depositInfo.address}
              className="qr-code"
            />
            <div className="common-input input-address">
              <label
                className={`${
                  isPhone ? Typo.p6 : isTablet ? Typo.p4 : Typo.p2
                } font-medium primary-text-color`}
                htmlFor="deposit-address"
              >
                {t('cashier.address')}
              </label>
              <input
                type="text"
                name="deposit-address"
                id="deposit-address"
                autoComplete="off"
                value={depositInfo.address}
                placeholder={t('placeholder.enterDepositAddress')}
                readOnly
              />
              <CopyOutlined
                className="icon-copy"
                onClick={() => handleCopyToClipboard(depositInfo.address)}
              />
            </div>
            {!!depositInfo.tag && (
              <div className="common-input input-address">
                <label
                  className={`${
                    isPhone ? Typo.p6 : isTablet ? Typo.p4 : Typo.p2
                  } font-medium primary-text-color`}
                  htmlFor="deposit-address"
                >
                  Memo
                </label>
                <input
                  type="text"
                  name="deposit-address"
                  id="deposit-address"
                  autoComplete="off"
                  value={depositInfo.tag}
                  placeholder={t('placeholder.enterDepositAddress')}
                  readOnly
                />
                <CopyOutlined
                  className="icon-copy"
                  onClick={() => handleCopyToClipboard(depositInfo.tag)}
                />
              </div>
            )}

            <ul className="deposite-notes">
              <li className="note">
                <ExclamationCircleOutlined />
                <p className={`${isPhone ? Typo.p6 : isTablet ? Typo.p4 : Typo.p2} font-normal`}>
                  {t('cashier.note.first', {
                    symbol: coins.find((coin) => coin.id === currentCoin)?.symbol || 'USD',
                  })}
                </p>
              </li>
              <li className="note">
                <ExclamationCircleOutlined />
                <p className={`${isPhone ? Typo.p6 : isTablet ? Typo.p4 : Typo.p2} font-normal`}>
                  {t('cashier.note.second')}
                </p>
              </li>
              <li className="note">
                <ExclamationCircleOutlined />
                <p className={`${isPhone ? Typo.p6 : isTablet ? Typo.p4 : Typo.p2} font-normal`}>
                  {t('cashier.note.third')}
                </p>
              </li>
            </ul>
          </>
        )}
        {activeTab === 2 && <FiatDepositTab closeModal={closeModal} resetTab={resetTab} />}
      </div>
      {contextHolder}
    </Modal>
  );
};

export default DepositModal;
